import { Separator } from "@/components/ui/separator";
import { ConfigurationTypeFile, FieldConfig } from "@/config/forms/general";
import { memo, useCallback } from "react";
import ImageField from "../form-fields/ImageField";
import InputSelectField from "../form-fields/InputSelectField";
import MultiSelectField from "../form-fields/MultiSelectField";
import RadioGroupField from "../form-fields/RadioGroupField";
import RatingField from "../form-fields/RatingField";
import SetField from "../form-fields/SetField";
import SwitchField from "../form-fields/SwitchField";
import TableFileField from "../form-fields/TableField";
import {
  Checkbox,
  LegacySelectCurrency,
  Text,
  RichText,
} from "../form-items/AbstractForm";

interface GenericFormProps {
  version?: string;
  getValues: (data: any) => any;
  control?: any;
  positionImage?: string;
  configuration: ConfigurationTypeFile;
  className?: string;
  id?: string;
}

const GenericForm = memo(
  ({
    className,
    getValues,
    version,
    control,
    configuration,
    positionImage,
    id,
  }: GenericFormProps) => {
    const renderField = useCallback(
      (configField: FieldConfig) => {
        const props = {
          config: configField,
          control,
          getValues,
          positionImage,
        };

        switch (configField.type) {
          case "text":
            return (
              <Text name={configField.name} disabled={configField.disabled} />
            );
          case "richtext":
            return (
              <RichText
                name={configField.name}
                disabled={configField.disabled}
              />
            );
          case "switch":
            return <SwitchField {...props} />;
          case "checkbox":
            return <Checkbox name={configField.name} />;
          case "link-simple":
          case "multiSelect":
            return <MultiSelectField {...props} />;
          case "rating":
            return <RatingField {...props} />;
          case "inputSelect":
            return <InputSelectField {...props} />;
          case "inputSelectCurrency":
            return <LegacySelectCurrency name={configField.name} />;
          case "image":
            return <ImageField {...props} />;
          case "set":
            return <SetField {...props} />;
          case "radioGroup":
            return <RadioGroupField {...props} />;
          case "table-file":
            return <TableFileField {...props} docId={id ?? ""} />;
          case "separator":
            return <Separator className={`my-4 ${configField.className}`} />;
          default:
            return null;
        }
      },
      [control, getValues, positionImage, id],
    );

    return (
      <form
        className={`w-full inline-flex flex-col justify-start ${className}`}
      >
        {version && (
          <p className="text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal text-start mb-5">
            {version}
          </p>
        )}
        <div className="inline-flex justify-between items-center gap-2 flex-wrap">
          {configuration.fields.map((configField, index) => (
            <div
              key={`${configField.name}-${index}`}
              className={configField.className}
            >
              {renderField(configField)}
            </div>
          ))}
        </div>
      </form>
    );
  },
);

GenericForm.displayName = "GenericForm";
export default GenericForm;
